import React, { useEffect } from 'react'
import { useFetch } from 'hooks/useFetch'
import SectionError from 'components/error/SectionError'
import moment from "moment";
import { convertRatiosDataBasedonYear } from 'helpers/TableComponentHelper'
import Loader from 'components/loader/Loader'
import { textBold } from 'utils/Constant'
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RatiosGraphContainer from 'components/wrapper/RatiosGraphContainer';
import { capitalize } from 'proptypes/StringProptype';

/* ── design tokens (matching TableContainer) ── */
const T = {
  bg: '#F6F5F0',
  card: '#FFFFFF',
  line: '#E5E2D8',
  ink: '#232821',
  mut: '#6E766C',
  acc: '#2E9E7A',
};

const RatiosTableContainer = (props) => {
  const { url, title } = props
  const { data: rawData, loading, error, retry } = useFetch(url)
  const data = rawData || []
  const [graphTitle, setGraphTitle] = React.useState()

  // Process data using the exact same helper
  const { columnHeaders, rows } = data.length
    ? convertRatiosDataBasedonYear(data)
    : { columnHeaders: [], rows: [] }

  return (
    <div style={{
      background: T.card,
      borderRadius: 16,
      border: `1px solid ${T.line}`,
      padding: '18px 20px',
      marginBottom: 16,
      overflowX: 'auto',
    }}>
      {/* Card header exactly like TableContainer */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <span style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 16,
          fontWeight: 700,
          color: T.ink,
        }}>
          {title}
        </span>
        <span style={{ fontSize: 11, color: T.mut }}>
          Consolidated · Figures in ₹ Cr
        </span>
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <SectionError message={error.message} onRetry={retry} />
      ) : data.length ? (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: "'Instrument Sans', system-ui, sans-serif",
        }}>
          {/* ── thead ── */}
          <thead>
            <tr>
              {columnHeaders && columnHeaders.map((col, i) => {
                // Ratios helper adds 'meta_data' and 'AHL' as the last two columns, hide them
                if (i === columnHeaders.length - 1 || i === columnHeaders.length - 2) return null;

                const isFirst = i === 0;
                const label = col.field;
                const display = isFirst ? label : moment(label).format('MMM YYYY');

                return (
                  <th key={i} style={{
                    textAlign: isFirst ? 'left' : 'right',
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    color: T.mut,
                    paddingBottom: 10,
                    borderBottom: `1px solid ${T.line}`,
                    paddingRight: 0,
                    whiteSpace: 'nowrap',
                    minWidth: isFirst ? 160 : 80,
                  }}>
                    {display.toUpperCase()}
                  </th>
                )
              })}
            </tr>
          </thead>

          {/* ── tbody ── */}
          <tbody>
            {rows.map((row, ri) => {
              const particulars = row['Ratios/Years'] || ''
              const isBold = textBold[particulars]
              return (
                <tr key={ri} style={{
                  borderBottom: `1px solid ${T.line}`,
                  backgroundColor: ri % 2 === 0 ? T.bg : 'transparent',
                }}>
                  {columnHeaders && columnHeaders.map((col, ci) => {
                    if (ci === columnHeaders.length - 1 || ci === columnHeaders.length - 2) return null;

                    const key = col.field;
                    const val = row[key];
                    const isFirst = ci === 0;

                    return (
                      <td key={ci} style={{
                        textAlign: isFirst ? 'left' : 'right',
                        fontSize: 13,
                        fontWeight: isBold ? 700 : 400,
                        color: T.ink,
                        padding: '9px 0',
                        whiteSpace: 'nowrap',
                      }}>
                        {isFirst ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {typeof val === 'string' ? capitalize(val.replace(/_/g, ' ')) : val}

                            {/* Info Tooltip (meta_data) */}
                            {row.meta_data && (
                              <Tooltip title={capitalize(row.meta_data)} arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      color: '#0d0d0d', fontSize: '12px', fontWeight: 600,
                                      bgcolor: '#D6E0FF',
                                      '& .MuiTooltip-arrow': { color: '#D6E0FF' },
                                    },
                                  },
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#8B8D8F', cursor: 'pointer' }} />
                              </Tooltip>
                            )}

                            {/* Graph Modal Trigger */}
                            <span onClick={() => setGraphTitle(val)} style={{ display: 'flex', alignItems: 'center' }}>
                              <RatiosGraphContainer title={graphTitle} onclick={() => setGraphTitle(val)} />
                            </span>
                          </div>
                        ) : (
                          val
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ color: T.mut, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default RatiosTableContainer